// ResetPassword.jsx - Password reset completion component for VibrantCast
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { Eye, EyeOff, AlertCircle, Check, Lock } from 'lucide-react';
import '../styles/Auth.css';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  
  const navigate = useNavigate();

  // Check for valid reset session
  useEffect(() => {
    const checkSession = async () => {
      try {
        // The user arrives here after clicking the email link
        // Supabase automatically handles the token exchange
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          setError('Invalid or expired reset link. Please request a new one.');
          setIsValidSession(false);
        } else if (session) {
          setIsValidSession(true);
        } else {
          // Check URL for recovery token (hash fragment)
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const type = hashParams.get('type');
          
          if (type === 'recovery' && accessToken) {
            // Session should be automatically established by Supabase
            const { data, error: sessionError } = await supabase.auth.getSession();
            if (sessionError || !data.session) {
              setError('Invalid or expired reset link. Please request a new one.');
              setIsValidSession(false);
            } else {
              setIsValidSession(true);
            }
          } else {
            setError('Invalid reset link. Please request a new password reset.');
            setIsValidSession(false);
          }
        }
      } catch (err) {
        console.error('Error checking session:', err);
        setError('An error occurred. Please request a new password reset.');
        setIsValidSession(false);
      } finally {
        setCheckingSession(false);
      }
    };

    checkSession();

    // Listen for auth state changes (Supabase handles the token exchange)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
        setCheckingSession(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let score = 0;
    let feedback = [];
    
    if (password.length >= 8) score++;
    else feedback.push('At least 8 characters');
    
    if (/[A-Z]/.test(password)) score++;
    else feedback.push('Uppercase letter');
    
    if (/[a-z]/.test(password)) score++;
    else feedback.push('Lowercase letter');
    
    if (/[0-9]/.test(password)) score++;
    else feedback.push('Number');
    
    if (/[^A-Za-z0-9]/.test(password)) score++;
    else feedback.push('Special character');
    
    return {
      score,
      feedback: feedback.length > 0 ? `Add: ${feedback.join(', ')}` : 'Strong password!'
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { password, confirmPassword } = formData;

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess(true);
      
      // Sign out after password reset to ensure clean login
      await supabase.auth.signOut();
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Password reset successful! Please log in with your new password.' }
        });
      }, 3000);
    } catch (error) {
      console.error('Password update error:', error);
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    const { score } = passwordStrength;
    if (score <= 2) return '#ef4444';
    if (score <= 3) return '#f59e0b';
    return '#22c55e';
  };

  const getPasswordStrengthWidth = () => {
    return `${(passwordStrength.score / 5) * 100}%`;
  };

  // Loading state
  if (checkingSession) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="loading-spinner-large"></div>
            <h2>Verifying...</h2>
            <p className="auth-subtitle">Please wait while we verify your reset link.</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid session state
  if (!isValidSession && !checkingSession) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="error-icon-large">
              <AlertCircle size={48} />
            </div>
            <h2>Link Expired</h2>
            <p className="auth-subtitle">
              {error || 'This password reset link is invalid or has expired.'}
            </p>
          </div>

          <div className="auth-actions">
            <Link to="/forgot-password" className="auth-button">
              Request New Link
            </Link>
            <Link to="/login" className="auth-button-secondary">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="success-icon-large">
              <Check size={48} />
            </div>
            <h2>Password Reset!</h2>
            <p className="auth-subtitle">
              Your password has been successfully reset. You'll be redirected to login shortly.
            </p>
          </div>

          <div className="auth-actions">
            <Link to="/login" className="auth-button">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo-container">
            <img 
              src="https://res.cloudinary.com/dh9jfogh1/image/upload/t_fitmax-300/v1729693346/vibrant-logo_ds6pwg.png" 
              alt="VibrantCast" 
              className="auth-logo-inline"
            />
          </div>
          <h2>Create New Password</h2>
          <p className="auth-subtitle">
            Enter your new password below
          </p>
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={20} />
            <div>
              <div className="error-title">Error</div>
              <p className="error-message">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="input-container">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                required
                disabled={loading}
                autoFocus
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.password && (
              <div className="password-strength">
                <div className="password-strength-bar">
                  <div 
                    className="password-strength-fill"
                    style={{ 
                      width: getPasswordStrengthWidth(),
                      backgroundColor: getPasswordStrengthColor()
                    }}
                  />
                </div>
                <span 
                  className="password-strength-text"
                  style={{ color: getPasswordStrengthColor() }}
                >
                  {passwordStrength.feedback}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="input-container">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <span className="password-mismatch">Passwords do not match</span>
            )}
            {formData.confirmPassword && formData.password === formData.confirmPassword && formData.confirmPassword.length > 0 && (
              <span className="password-match">
                <Check size={14} /> Passwords match
              </span>
            )}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading || (formData.password !== formData.confirmPassword) || formData.password.length < 8}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
